import React, { Fragment, useRef, useState } from "react";
import { Link } from "react-router-dom";
import swal from "sweetalert";
import { Button, Modal } from "react-bootstrap";

import { db, storage } from "../../../../firebase";
import { ref, getDownloadURL } from "firebase/storage";
import {
  where,
  query,
  collection,
  doc,
  updateDoc,
  deleteField,
  getDoc,
} from "firebase/firestore";
import { useAuth } from "../../../../context/AuthContext";
import PageTitle from "../../../layouts/PageTitle";
import { usePagination } from "../../../../hook/usePagination";
import CpapTrialExternalRepairItem from "./CpapTrialExternalRepairItem";
import { CpapTrialExternalRepairForm } from "./CpapTrialExternalRepairForm";

const CpapTrialExternalRepairList = () => {
  const { currentUser } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [editTicket, setEditTicket] = useState(null);
  const [searchItem, setSearchItem] = useState("");

  // Image viewing states
  const [imageModal, setImageModal] = useState(false);
  const [fileUrl, setFileUrl] = useState(null);
  const [isImageLoading, setIsImageLoading] = useState(false);

  const searchRef = useRef(null);
  const externalRepairInputRef = useRef({
    vendor: "",
    sentDate: "",
    details: "",
    notes: "",
    repairStatus: "",
  });

  const itemPerPage = 50;

  // Query for tickets with external repair
  let queryRef = query(
    collection(db, "cpapTrialServiceTickets"),
    where("hasExternalRepair", "==", true),
  );

  const {
    items: externalRepairList,
    isStart,
    isEnd,
    getPrev,
    getNext,
    totalItems,
    totalPages,
    currentPage,
    goToPage,
  } = usePagination(queryRef, { limit: itemPerPage });

  const onSearchChange = () => {
    setSearchItem(searchRef.current.value);
  };

  const handleOnEdit = (id) => {
    const ticket = externalRepairList.find((item) => item.id === id);
    setEditTicket(ticket);
    const extRepair =
      ticket && ticket.externalRepair ? ticket.externalRepair : {};
    externalRepairInputRef.current = {
      vendor: extRepair.vendor || "",
      sentDate: extRepair.sentDate || "",
      details: extRepair.details || "",
      notes: extRepair.notes || "",
      repairStatus: extRepair.repairStatus || "",
    };
    setShowModal(true);
  };

  const handleChange = (name) => (e) => {
    externalRepairInputRef.current[name] = e.target.value;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!editTicket) {
      swal("Error", "No ticket selected", "error");
      return;
    }

    if (!externalRepairInputRef.current.vendor) {
      swal("Error", "กรุณากรอกชื่อบริษัทที่ส่งซ่อม", "error");
      return;
    }
    if (!externalRepairInputRef.current.sentDate) {
      swal("Error", "กรุณากรอกวันที่ส่งบริษัทนอก", "error");
      return;
    }
    if (!externalRepairInputRef.current.repairStatus) {
      swal("Error", "กรุณาเลือกสถานะ", "error");
      return;
    }

    try {
      const repairData = {
        externalRepair: {
          externalRepairNo:
            (editTicket &&
              editTicket.externalRepair &&
              editTicket.externalRepair.externalRepairNo) ||
            "",
          vendor: externalRepairInputRef.current.vendor,
          sentDate: externalRepairInputRef.current.sentDate,
          details: externalRepairInputRef.current.details || "",
          notes: externalRepairInputRef.current.notes || "",
          repairStatus: externalRepairInputRef.current.repairStatus,
          createdAt:
            (editTicket &&
              editTicket.externalRepair &&
              editTicket.externalRepair.createdAt) ||
            new Date(),
          createdBy:
            (editTicket &&
              editTicket.externalRepair &&
              editTicket.externalRepair.createdBy) ||
            currentUser.displayName,
          updatedAt: new Date(),
          updatedBy: currentUser.displayName,
        },
        updatedAt: new Date(),
        updatedBy: currentUser.displayName,
      };

      // Update service status based on repair status
      if (externalRepairInputRef.current.repairStatus === "เสร็จสิ้น") {
        repairData.serviceStatus = "เสร็จสิ้น";
      } else if (externalRepairInputRef.current.repairStatus === "ยกเลิก") {
        repairData.serviceStatus = "ยกเลิก";
      }

      await updateDoc(
        doc(db, "cpapTrialServiceTickets", editTicket.id),
        repairData
      );

      // Update CPAP device status when repair is finished
      if (
        externalRepairInputRef.current.repairStatus === "เสร็จสิ้น" ||
        externalRepairInputRef.current.repairStatus === "ยกเลิก"
      ) {
        const cpapRef = doc(db, "cpaps", editTicket.sn);
        const cpapSnap = await getDoc(cpapRef);
        if (cpapSnap.exists()) {
          await updateDoc(cpapRef, {
            Status: "ว่าง",
            Activity: "ส่งซ่อม (เสร็จสิ้น)",
            UpdatedDateTime: new Date(),
          });
        }
      }

      handleOnHide();
      swal("Success", "บันทึกข้อมูลเรียบร้อย", "success");
    } catch (error) {
      console.error("Error updating external repair:", error);
      swal("Error", "Failed to update external repair data", "error");
    }
  };

  const handleOnHide = () => {
    setShowModal(false);
    setEditTicket(null);
    externalRepairInputRef.current = {
      vendor: "",
      sentDate: "",
      details: "",
      notes: "",
      repairStatus: "",
    };
  };

  const handleOnViewFile = (id, file) => {
    setIsImageLoading(true);
    getDownloadURL(
      ref(storage, "cpapTrialServiceImage/" + id + "/" + file)
    ).then((url) => {
      setFileUrl(url);
      setIsImageLoading(false);
      setImageModal(true);
    });
  };

  const handleCloseImageModal = () => {
    setImageModal(false);
    setFileUrl(null);
  };

  const handleOnDelete = async (id) => {
    swal({
      title: "ยืนยันการลบ?",
      text: "ต้องการลบข้อมูลส่งซ่อมบริษัทนอกหรือไม่?",
      icon: "warning",
      buttons: ["ยกเลิก", "ลบ"],
      dangerMode: true,
    }).then(async (willDelete) => {
      if (willDelete) {
        try {
          await updateDoc(doc(db, "cpapTrialServiceTickets", id), {
            hasExternalRepair: false,
            externalRepair: deleteField(),
            serviceStatus: "กำลังดำเนินการ",
            updatedAt: new Date(),
            updatedBy: currentUser.displayName,
          });
          swal("สำเร็จ", "ลบข้อมูลเรียบร้อย", "success");
        } catch (error) {
          console.error("Error deleting external repair:", error);
          swal("Error", "Failed to delete external repair data", "error");
        }
      }
    });
  };

  const handleExportExternalRepair = async (ticketId) => {
    // PDF export stub - can be implemented later
    swal("Info", "PDF export will be available soon", "info");
  };

  return (
    <Fragment>
      <PageTitle
        activeMenu="CPAP Trial External Repair List"
        motherMenu="CPAP Trial"
      />

      {/* Edit Modal */}
      <Modal
        className="fade bd-example-modal-lg"
        show={showModal}
        onHide={handleOnHide}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>แก้ไขข้อมูลส่งซ่อมบริษัทนอก</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editTicket && (
            <div className="mb-3 p-3 bg-light rounded">
              <p className="mb-1">
                <strong>Service Order:</strong> {editTicket.serviceOrderNumber}
              </p>
              <p className="mb-0">
                <strong>Equipment:</strong> {editTicket.brand}{" "}
                {editTicket.model} (SN: {editTicket.sn})
              </p>
            </div>
          )}

          {editTicket && editTicket.serviceType && (
            <div className="form-group row m-1">
              <label className="col-lg-4 col-form-label">ประเภท</label>
              <div className="col-lg-6">
                <input
                  type="text"
                  className="form-control"
                  value={editTicket.serviceType}
                  disabled
                />
              </div>
            </div>
          )}

          {editTicket && editTicket.accessories && (
            <div className="form-group row m-1">
              <label className="col-lg-4 col-form-label">รายการอุปกรณ์</label>
              <div className="col-lg-6">
                <textarea
                  className="form-control"
                  value={editTicket.accessories}
                  disabled
                  rows="2"
                />
              </div>
            </div>
          )}

          {editTicket && editTicket.image && editTicket.image.length > 0 && (
            <div className="form-group row m-1">
              <label className="col-lg-4 col-form-label">รูปภาพ</label>
              <div className="col-lg-6">
                {editTicket.image.map((img, idx) => (
                  <Link
                    key={idx}
                    to="#"
                    onClick={() => handleOnViewFile(editTicket.id, img)}
                    className="mr-2 d-block"
                  >
                    <i className="fa fa-image"></i> {img}
                  </Link>
                ))}
              </div>
            </div>
          )}

          <CpapTrialExternalRepairForm
            onChange={handleChange}
            initialValues={editTicket && editTicket.externalRepair}
          />
        </Modal.Body>
        <div className="d-flex justify-content-center mb-3">
          <Button className="sm" onClick={handleSubmit}>
            บันทึก
          </Button>
          <Button className="sm ml-2" variant="light" onClick={handleOnHide}>
            ยกเลิก
          </Button>
        </div>
      </Modal>

      {/* Image View Modal */}
      {!isImageLoading && (
        <Modal
          className="fade bd-example-modal-lg"
          show={imageModal}
          onHide={handleCloseImageModal}
          size="xl"
          backdrop={true}
          keyboard={true}
        >
          <Modal.Body className="m-0 p-0">
            <div
              className="card-body mx-0 p-0 col-sm-12"
              style={{ height: "80vh" }}
            >
              {fileUrl && (
                <img
                  src={fileUrl}
                  alt="Ticket Image"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                  }}
                />
              )}
            </div>
          </Modal.Body>
        </Modal>
      )}

      <div className="form-head d-flex mb-3 mb-lg-5 align-items-start">
        <div className="mr-auto">
          <h2 className="text-black font-w600 mb-0">
            CPAP Trial External Repair
          </h2>
        </div>
        <div className="input-group search-area d-inline-flex">
          <input
            type="text"
            className="form-control"
            placeholder="Search..."
            ref={searchRef}
            onChange={onSearchChange}
          />
          <div className="input-group-append">
            <span className="input-group-text c-pointer">
              <i className="flaticon-381-search-2"></i>
            </span>
          </div>
        </div>
      </div>

      <CpapTrialExternalRepairItem
        tickets={externalRepairList}
        onEdit={handleOnEdit}
        onDelete={handleOnDelete}
        onExport={handleExportExternalRepair}
        totalItems={totalItems}
        totalPages={totalPages}
        currentPage={currentPage}
        itemsPerPage={itemPerPage}
        goToPage={goToPage}
        isStart={isStart}
        isEnd={isEnd}
        getNext={getNext}
        getPrev={getPrev}
      />
    </Fragment>
  );
};

export default CpapTrialExternalRepairList;
